import { BASE_PATH } from "@/config/basePath";
import type { LocaleType } from "@/constants/locales";
import type { LinkEntryPoint } from "@/lib/analytics/tracking-utils";
import {
  MAX_LOCATION_SLUG_LENGTH,
  MIN_LOCATION_SLUG_LENGTH,
  RESERVED_DISCOVER_SEGMENTS,
} from "@/constants/reservedSlugs";
import { isMapAreaId } from "@/utils/location";
import { deslugify, slugify } from "@/utils/slugs";

/** Query params that move the search to a point: its coordinates and, for a free place, its name. */
export const CUSTOM_LOCATION_PARAMS = ["lat", "lon", "locationName"] as const;

/** Slug for a point whose name can't be a slug: a failed lookup, "Å", a non-Latin name. */
export const NEAR_ME_SLUG = "near-me";

/**
 * Build discovery URL with location, optional activity and optional query
 */
export function buildDiscoverUrl({
  locale,
  locationSlug,
  activitySlug,
  query,
}: {
  locale: LocaleType;
  locationSlug: string;
  activitySlug?: string;
  query?: URLSearchParams;
}): string {
  const path = activitySlug
    ? `/${locale}/${locationSlug}/${activitySlug}`
    : `/${locale}/${locationSlug}`;
  const search = query?.toString();
  return search ? `${path}?${search}` : path;
}

function isReadableSlug(slug: string): boolean {
  return (
    /[a-z]/.test(slug) &&
    slug.length >= MIN_LOCATION_SLUG_LENGTH &&
    slug.length <= MAX_LOCATION_SLUG_LENGTH &&
    !RESERVED_DISCOVER_SEGMENTS.has(slug)
  );
}

/** A name with at least one letter, so raw coordinates ("59.9, 10.7") never pass as a place name. */
export function isPlaceName(name: string | undefined): name is string {
  return name !== undefined && /\p{L}/u.test(name);
}

/**
 * Build full canonical URL for metadata
 */
export function buildCanonicalUrl(
  baseUrl: string,
  locale: LocaleType,
  locationSlug: string,
  activitySlug?: string,
): string {
  const path = activitySlug
    ? `/${locale}/${locationSlug}/${activitySlug}`
    : `/${locale}/${locationSlug}`;
  return `${baseUrl}${BASE_PATH}${path}`;
}

/**
 * Link to an event's detail page. `ref` says where the link is, so the page
 * can tell where the visitor came from (`getEntryPoint`). `params` are added
 * after it.
 */
export function buildEventHref(
  locale: string,
  eventId: string,
  ref: LinkEntryPoint,
  params: Record<string, string> = {},
): string {
  const query = new URLSearchParams({ ref, ...params });
  return `/${locale}/events/${eventId}?${query}`;
}

export function buildClubHref(
  locale: string,
  club: { id: string; shortId: string },
  ref: LinkEntryPoint,
  params: Record<string, string> = {},
): string {
  const query = new URLSearchParams({ ref, ...params });
  return `/${locale}/clubs/${club.id}/${encodeURIComponent(club.shortId)}?${query}`;
}

/** What a place name may have between words: "St. Moritz", "L'Aquila", "Majorstuen, Oslo". */
const NAME_SEPARATOR = /[\s,.'’-]/u;

/**
 * The name is its slug with accents, case and separators: every other
 * character leaves a trace in the slug. `slugify` drops what it can't spell,
 * so "Lillehammer 東京" and "Oslo!!!" would otherwise match `lillehammer` and `oslo`.
 */
function spellsSlug(name: string, slug: string): boolean {
  return (
    slugify(name) === slug &&
    [...name.normalize("NFC")].every(
      (char) => NAME_SEPARATOR.test(char) || slugify(char) !== "",
    )
  );
}

/**
 * The query's name when it is this slug's place with its accents and case
 * ("Ålesund" for `alesund`), so a link can't put other text on the page; the
 * slug read as words otherwise. A point on `near-me` has no slug to match.
 */
export function placeName(slug: string, queryName: string): string {
  if (slug === NEAR_ME_SLUG) return nearMeName(queryName);
  return spellsSlug(queryName, slug) ? queryName : deslugify(slug);
}

/** Longest name a `near-me` page shows; place names are shorter. */
const MAX_NEAR_ME_NAME_LENGTH = 60;

/**
 * A `near-me` point keeps a name only for the reasons it is on `near-me`: the
 * name can't be a slug ("Å", "東京"), or it is an area's name used far from
 * that area. Any other text in the link is not shown.
 */
function nearMeName(queryName: string): string {
  const slug = slugify(queryName);
  const explainsNearMe =
    (isMapAreaId(slug) && spellsSlug(queryName, slug)) || !isReadableSlug(slug);
  return explainsNearMe && queryName.length <= MAX_NEAR_ME_NAME_LENGTH
    ? queryName
    : "";
}
